import React, {useState, useEffect} from 'react';
import profileImage from "../../assets/images/default_profile_img.png";
import { useAuth0 } from '@auth0/auth0-react';
import {loadProfileData, postProfileData}  from "../../utils/profileUtils";
import { useCards, useActions } from "../../utils/reactQueryHooks";
import {
    exportAllData,
    exportAllCards,
    exportAllActions,
    exportIndividualSensor,
    exportIndividualActuator
} from "../../utils/exportUtils";
import "../../styles/unifiedCards.css";

export default function Settings() {
    const { user } = useAuth0();
    const { data: cards = [] } = useCards();
    const { data: actions = [] } = useActions();

    const [settings, setSettings] = useState({
        username: user?.nickname ?? '',
        email: user?.email ?? '',
        phone: user?.phone_number ?? '',
        notifications: false,
        location: user?.locale ?? '',
        admins: '',
        contributors: ''
    });

    // Export state
    const [exportFormat, setExportFormat] = useState('json');
    const [exportTimeRange, setExportTimeRange] = useState('14d'); // Default to 2 weeks
    const [selectedSensor, setSelectedSensor] = useState('');
    const [selectedActuator, setSelectedActuator] = useState('');
    const [isExporting, setIsExporting] = useState(false);

    const handleInputChange = (e) => {
        const {name, value, type, checked} = e.target;

        setSettings((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    }

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            //Post updated settings to server:
            await postProfileData(); //gets directly from cache!
            alert("Settings updated successfully!");
        }
        catch (error) {
            console.error("Failed to update settings:", error);
            alert("Failed to update settings. Please try again");
        }
    }

    //Fetch profile data on first load:
    useEffect(() => {
        const fetchData = async () => {
            const {data: profileData} = await loadProfileData(user?.nickname);

            if (profileData) {                
                // Normalize the profile data to match the initial state structure
                const normalizedProfileData = {
                    username: profileData.username || user?.nickname || '',
                    email: profileData.email || user?.email || '',
                    phone: profileData.phone || user?.phone_number || '',
                    notifications: profileData.notifications === 1 ? true : 
                                   profileData.notifications === true ? true : 
                                   false,
                    location: profileData.location || user?.locale || '',
                    admins: profileData.admins || '',
                    contributors: profileData.contributors || ''
                };
                setSettings(normalizedProfileData);
            }
        };

        fetchData();
    }, [user?.nickname]);

    // Export handlers
    const handleExportAll = async () => {
        setIsExporting(true);
        try {
            await exportAllData(cards, actions, exportFormat, exportTimeRange);
        } catch (error) {
            console.error('Export error:', error);
            alert('Failed to export data. Please try again.');
        } finally {
            setIsExporting(false);
        }
    };

    const handleExportAllSensors = async () => {
        setIsExporting(true);
        try {
            await exportAllCards(cards, exportFormat);
        } catch (error) {
            console.error('Export error:', error);
            alert('Failed to export sensors. Please try again.');
        } finally {
            setIsExporting(false);
        }
    };

    const handleExportAllActuators = async () => {
        setIsExporting(true);
        try {
            await exportAllActions(actions, exportFormat);
        } catch (error) {
            console.error('Export error:', error);
            alert('Failed to export actuators. Please try again.');
        } finally {
            setIsExporting(false);
        }
    };

    const handleExportIndividualSensor = async () => {
        if (!selectedSensor) {
            alert('Please select a sensor to export');
            return;
        }
        
        const card = cards.find(c => c.macAddress === selectedSensor);
        if (!card) {
            alert('Selected sensor not found');
            return;
        }

        setIsExporting(true);
        try {
            // Show a message that we're fetching data from InfluxDB
            console.log(`Exporting sensor data: ${card.sensorDescription || card.macAddress} for ${exportTimeRange}`);
            await exportIndividualSensor(card, exportFormat, exportTimeRange);
        } catch (error) {
            console.error('Export error:', error);
            alert('Failed to export sensor data. Please check the console for details and try again.');
        } finally {
            setIsExporting(false);
        }
    };

    const handleExportIndividualActuator = async () => {
        if (!selectedActuator) {
            alert('Please select an actuator to export');
            return;
        }
        
        const action = actions.find(a => a.macAddress === selectedActuator);
        if (!action) {
            alert('Selected actuator not found');
            return;
        }

        setIsExporting(true);
        try {
            // Show a message that we're fetching data from InfluxDB
            console.log(`Exporting actuator data: ${action.actuatorDescription || action.macAddress} for ${exportTimeRange}`);
            await exportIndividualActuator(action, exportFormat, exportTimeRange);
        } catch (error) {
            console.error('Export error:', error);
            alert('Failed to export actuator data. Please check the console for details and try again.');
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div className="settings-container">
            {/* Hero Section */}
            <div className="settings-hero">
                <div className="settings-hero-content">
                    <div className="settings-image-container">
                        <img className="settings-image" src={profileImage} alt="Settings" />
                    </div>
                    <h1 className="settings-title">Settings</h1>
                    <p className="settings-subtitle">Welcome, {settings.username}</p>
                </div>
            </div>
            
            {/* Main Content */}
            <div className="settings-content">
                <form className="settings-form" onSubmit={handleSave}>
                    <div className="settings-form-content">
                        {/* Personal Information Section */}
                        <section className="settings-section">
                            <h2 className="settings-section-title">Personal Information</h2>
                            <div className="settings-fields-grid">
                                <div className="settings-field">
                                    <label htmlFor="email">Email</label>
                                    <input 
                                        type="text" 
                                        id="email"
                                        name="email" 
                                        value={settings.email} 
                                        onChange={handleInputChange}
                                        pattern="^[^\s@]+@[^\s@]+\.[^\s@]+$"
                                    />
                                </div>
                                
                                <div className="settings-field">
                                    <label htmlFor="phone">Phone</label>
                                    <input 
                                        type="text" 
                                        id="phone"
                                        name="phone" 
                                        value={settings.phone} 
                                        onChange={handleInputChange}
                                        pattern="^\+?[1-9]\d{0,14}$"
                                        placeholder="123-456-7890"
                                    />
                                </div>
                                
                                <div className="settings-field">
                                    <label htmlFor="location">Location</label>
                                    <input 
                                        type="text" 
                                        id="location"
                                        name="location" 
                                        value={settings.location} 
                                        onChange={handleInputChange}
                                        placeholder="Location of site"
                                    />
                                </div>
                                
                                <div className="settings-field settings-checkbox-field">
                                    <label htmlFor="notifications">Notifications</label>
                                    <input 
                                        type="checkbox" 
                                        id="notifications"
                                        name="notifications" 
                                        checked={settings.notifications}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>
                        </section>
                        
                        {/* Team Management Section */}
                        <section className="settings-section">
                            <h2 className="settings-section-title">Team Management</h2>
                            <div className="settings-fields-grid">
                                <div className="settings-field">
                                    <label htmlFor="admins">Admins</label>
                                    <textarea 
                                        id="admins"
                                        name="admins" 
                                        value={settings.admins} 
                                        onChange={handleInputChange}
                                        placeholder='Add admin emails separated by comma'
                                        rows="3"
                                    ></textarea>
                                </div>
                                
                                <div className="settings-field">
                                    <label htmlFor="contributors">Contributors</label>
                                    <textarea 
                                        id="contributors"
                                        name="contributors" 
                                        value={settings.contributors} 
                                        onChange={handleInputChange}
                                        placeholder='Add contributor emails separated by comma'
                                        rows="3"
                                    ></textarea>
                                </div>
                            </div>
                        </section>
                    </div>
                    
                    {/* Data Export Section - Separate Grouping */}
                    <div className="settings-export-section-wrapper">
                        <section className="settings-section settings-export-section">
                            <h2 className="settings-section-title">Data Export</h2>
                            <p className="settings-section-description">
                                Export your sensor and actuator data in various formats. Historical data exports include time-series data for analysis.
                            </p>
                            
                            {/* Export Format Selection */}
                            <div className="settings-export-controls">
                                <div className="settings-export-group">
                                    <label htmlFor="exportFormat">Export Format:</label>
                                    <select
                                        id="exportFormat"
                                        value={exportFormat}
                                        onChange={(e) => setExportFormat(e.target.value)}
                                        className="settings-select"
                                    >
                                        <option value="json">JSON</option>
                                        <option value="csv">CSV</option>
                                    </select>
                                </div>

                                <div className="settings-export-group">
                                    <label htmlFor="exportTimeRange">Time Range (for historical data):</label>
                                    <select
                                        id="exportTimeRange"
                                        value={exportTimeRange}
                                        onChange={(e) => setExportTimeRange(e.target.value)}
                                        className="settings-select"
                                        title="Select how much historical data to include in the export (data is fetched from InfluxDB)"
                                    >
                                        <option value="7d">Last 7 days</option>
                                        <option value="14d">Last 14 days (2 weeks)</option>
                                        <option value="28d">Last 28 days (4 weeks)</option>
                                        <option value="30d">Last 30 days</option>
                                    </select>
                                    <small style={{display: 'block', marginTop: '0.25rem', fontSize: '0.8rem', opacity: 0.8, color: 'var(--text-color-secondary)'}}>
                                        Applies to individual sensor/actuator exports
                                    </small>
                                </div>
                            </div>

                            {/* Export Options */}
                            <div className="settings-export-options">
                                <div className="settings-export-option">
                                    <h3 className="settings-export-option-title">Export All Data</h3>
                                    <p className="settings-export-option-description">
                                        Export all sensors and actuators with summary information
                                    </p>
                                    <button
                                        type="button"
                                        onClick={handleExportAll}
                                        disabled={isExporting || (cards.length === 0 && actions.length === 0)}
                                        className="green-button settings-export-button"
                                    >
                                        {isExporting ? 'Exporting...' : 'Export All Data'}
                                    </button>
                                </div>

                                <div className="settings-export-option">
                                    <h3 className="settings-export-option-title">Export All Sensors</h3>
                                    <p className="settings-export-option-description">
                                        Export all sensor configurations ({cards.length} sensors)
                                    </p>
                                    <button
                                        type="button"
                                        onClick={handleExportAllSensors}
                                        disabled={isExporting || cards.length === 0}
                                        className="green-button settings-export-button"
                                    >
                                        {isExporting ? 'Exporting...' : 'Export All Sensors'}
                                    </button>
                                </div>

                                <div className="settings-export-option">
                                    <h3 className="settings-export-option-title">Export All Actuators</h3>
                                    <p className="settings-export-option-description">
                                        Export all actuator configurations ({actions.length} actuators)
                                    </p>
                                    <button
                                        type="button"
                                        onClick={handleExportAllActuators}
                                        disabled={isExporting || actions.length === 0}
                                        className="green-button settings-export-button"
                                    >
                                        {isExporting ? 'Exporting...' : 'Export All Actuators'}
                                    </button>
                                </div>

                                <div className="settings-export-option">
                                    <h3 className="settings-export-option-title">Export Individual Sensor</h3>
                                    <p className="settings-export-option-description">
                                        Export a specific sensor with its historical data
                                    </p>
                                    <select
                                        value={selectedSensor}
                                        onChange={(e) => setSelectedSensor(e.target.value)}
                                        className="settings-select settings-export-select"
                                        disabled={cards.length === 0}
                                    >
                                        <option value="">Select a sensor...</option>
                                        {cards.map(card => (
                                            <option key={card.macAddress} value={card.macAddress}>
                                                {card.sensorDescription || card.macAddress}
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        type="button"
                                        onClick={handleExportIndividualSensor}
                                        disabled={isExporting || !selectedSensor || cards.length === 0}
                                        className="green-button settings-export-button"
                                    >
                                        {isExporting ? 'Exporting...' : 'Export Sensor'}
                                    </button>
                                </div>

                                <div className="settings-export-option">
                                    <h3 className="settings-export-option-title">Export Individual Actuator</h3>
                                    <p className="settings-export-option-description">
                                        Export a specific actuator with its historical data
                                    </p>
                                    <select
                                        value={selectedActuator}
                                        onChange={(e) => setSelectedActuator(e.target.value)}
                                        className="settings-select settings-export-select"
                                        disabled={actions.length === 0}
                                    >
                                        <option value="">Select an actuator...</option>
                                        {actions.map(action => (
                                            <option key={action.macAddress} value={action.macAddress}>
                                                {action.actuatorDescription || action.macAddress} ({action.actuatorType})
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        type="button"
                                        onClick={handleExportIndividualActuator}
                                        disabled={isExporting || !selectedActuator || actions.length === 0}
                                        className="green-button settings-export-button"
                                    >
                                        {isExporting ? 'Exporting...' : 'Export Actuator'}
                                    </button>
                                </div>
                            </div>
                        </section>
                    </div>
                    
                    <div className="settings-actions">
                        <button type="submit" className="green-button">Save Changes</button>
                    </div>
                </form>
            </div>
        </div>
    )
}

